/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import { useCallback, useEffect, useState } from 'react';
import { useBestFitQuery } from '../../Hooks/Api/useBestFitQuery';

/**
 * Custom useOccupancySpots Hook
 */
export const useOccupancySpots = (studioId) => {
  // Get the utilization data, refetch every 60 seconds
  const { data, isLoading, isError } = useBestFitQuery('utilization', null, { studioId }, { refetchInterval: 60000 });
  const [studioOccupancy, setStudioOccupancy] = useState(0);

  /**
     * Update the studio occupancy
     */
  const updateStudioOccupancy = useCallback(() => {
    const currentTimeSlot = data.items.find((timeSlot) => timeSlot.isCurrent);
    // Studio is open
    if (currentTimeSlot) {
      setStudioOccupancy(currentTimeSlot.percentage);
      // Studio is closed
    } else {
      setStudioOccupancy(0);
    }
  }, [data]);

  /**
     * Initialize studio occupancy
     */
  useEffect(() => {
    if (!isLoading && data) {
      updateStudioOccupancy();
    }
  }, [data, isLoading, updateStudioOccupancy]);

  return {
    isLoading,
    studioOccupancy,
    isError,
  };
};
